import axios from 'axios';

import {
  BannerInput,
  BannerPlacementsResponse,
  BannerUpload,
  BannerUploadRequest,
  CampaignDetail,
  CampaignInput,
  CampaignListItem,
  Segment,
  SegmentListResponse,
} from 'app/models/BannerCampaign';

const API_URL = process.env.REACT_APP_CUSTOMER_SEGMENTATION_API_URL;

/**
 * Sem a variável definida, toda requisição iria para "undefined/campaigns" e
 * falharia como 404 sem explicar o motivo. Falhar aqui diz o que está
 * faltando.
 */
const baseUrl = (): string => {
  if (!API_URL) {
    throw new Error('REACT_APP_CUSTOMER_SEGMENTATION_API_URL is not set');
  }
  return API_URL;
};

// -- Configuração de placements ---------------------------------------------

export const getBannerPlacements =
  async (): Promise<BannerPlacementsResponse> => {
    const {data} = await axios.get(`${baseUrl()}/banner-placements`);
    return data;
  };

// -- Campanhas --------------------------------------------------------------

export const getBannerCampaigns = async (params?: {
  is_active?: boolean;
  is_current?: boolean;
  page?: number;
  items_per_page?: number;
}): Promise<{campaigns: CampaignListItem[]; total: number}> => {
  const {data} = await axios.get(`${baseUrl()}/campaigns`, {params});
  return data;
};

export const getBannerCampaign = async (
  uid: string
): Promise<CampaignDetail> => {
  const {data} = await axios.get(`${baseUrl()}/campaigns/${uid}`);
  return data;
};

export const createBannerCampaign = async (
  input: CampaignInput
): Promise<CampaignDetail> => {
  const {data} = await axios.post(`${baseUrl()}/campaigns`, input);
  return data;
};

export const updateBannerCampaign = async (
  uid: string,
  input: Partial<CampaignInput>
): Promise<CampaignDetail> => {
  const {data} = await axios.put(`${baseUrl()}/campaigns/${uid}`, {
    uid,
    ...input,
  });
  return data;
};

// -- Banners ---------------------------------------------------------------

export const createBannerUploads = async (
  campaignUid: string,
  assets: BannerUploadRequest[]
): Promise<{uploads: BannerUpload[]}> => {
  const {data} = await axios.post(
    `${baseUrl()}/campaigns/${campaignUid}/banners/uploads`,
    {assets}
  );
  return data;
};

/**
 * Instância limpa, sem os interceptors do painel.
 *
 * `setupAxios` em app/modules/auth/core/AuthHelpers.ts registra o interceptor
 * de Authorization na instância **global** do axios, então `axios.put` — mesmo
 * "cru" — sai com `Authorization: Bearer <token>`. O S3 recusa isso com
 * `400 InvalidArgument: Only one auth mechanism allowed`, porque a URL
 * assinada já autentica pela query string.
 *
 * Interceptors são por instância, e `axios.create()` não herda os da global.
 * Existe a convenção `Authorization: 'ANONYMOUS'`, que o interceptor apaga,
 * mas ela só funciona quando há token: sem login o literal 'ANONYMOUS' iria
 * para o S3 e daria o mesmo 400.
 */
const s3Client = axios.create();

/**
 * Envia o arquivo direto ao S3 pela URL assinada. A falha chega como o axios
 * a entrega, com o XML do S3 no corpo: quem a traduz em
 * "Falha no envio ao S3 (<Code>)" é o envio do banner (`submitBanner`).
 */
export const uploadBannerAsset = async (
  uploadUrl: string,
  file: File
): Promise<void> => {
  await s3Client.put(uploadUrl, file, {
    headers: {'Content-Type': file.type},
    transformRequest: [(data) => data],
  });
};

export const createBanner = async (
  campaignUid: string,
  input: BannerInput
): Promise<{uid: string}> => {
  const {data} = await axios.post(
    `${baseUrl()}/campaigns/${campaignUid}/banners`,
    input
  );
  return data;
};

export const updateBanner = async (
  campaignUid: string,
  bannerUid: string,
  input: Omit<BannerInput, 'assets'>
): Promise<{uid: string}> => {
  const {data} = await axios.put(
    `${baseUrl()}/campaigns/${campaignUid}/banners/${bannerUid}`,
    input
  );
  return data;
};

export const reorderBanners = async (
  campaignUid: string,
  order: {banner_uid: string; order: number}[]
): Promise<void> => {
  await axios.put(`${baseUrl()}/campaigns/${campaignUid}/banners/order`, {
    order,
  });
};

// -- Públicos (segmentos) --------------------------------------------------

export const getSegments = async (params?: {
  is_active?: boolean;
  has_clients?: boolean;
  search?: string;
  page?: number;
  items_per_page?: number;
}): Promise<SegmentListResponse> => {
  const {data} = await axios.get(`${baseUrl()}/segments`, {params});
  return data;
};

export const createSegment = async (name: string): Promise<Segment> => {
  const {data} = await axios.post(`${baseUrl()}/segments`, {name});
  return data;
};

/**
 * Substitui a lista inteira de oficinas do público — não é incremental. A
 * tela precisa avisar antes de confirmar.
 */
export const replaceSegmentClients = async (
  segmentUid: string,
  clientUids: string[]
): Promise<void> => {
  await axios.put(`${baseUrl()}/segments/${segmentUid}/clients`, {
    client_uids: clientUids,
  });
};
